import React, { useEffect, useState, useRef } from "react";
import { VList } from "../VList";
import { Bell, BellRing, PawPrint } from "lucide-react";
import { Avatar } from "../Avatar";
import { pushSupported, pushStatus, enablePush, disablePush } from "../../lib/push";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function NotificationBell({ open, onToggle, onClose, notifications, unread, navigate }) {
  const [push, setPush] = useState("disabled");
  const [pushBusy, setPushBusy] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (open && pushSupported()) pushStatus().then(setPush).catch(() => {});
  }, [open]);

  // Close the panel when clicking anywhere outside the bell + its dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, onClose]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push === "enabled") { await disablePush(); setPush("disabled"); }
      else { await enablePush(); setPush("enabled"); toast.success("Notifications enabled — you'll hear from the pack even when you're away."); }
    } catch (e) {
      setPush(Notification.permission === "denied" ? "denied" : "disabled");
      if (e?.code === "denied") toast.error("Alerts are blocked in this browser. Open Pack Signals for steps to re-enable them.", { duration: 8000 });
      else if (e?.code === "dismissed") toast("No choice was made — tap again when you're ready to allow alerts.");
      else toast.error(e?.message || "Couldn't change push alerts — please try again.");
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        data-testid="notification-bell"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        onClick={onToggle}
        className="relative p-2 rounded-full hover:bg-slate-800/70 text-slate-300 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-slate-950 text-[10px] font-bold rounded-full min-w-[16px] h-4 px-0.5 flex items-center justify-center" data-testid="bell-unread-count">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed top-[calc(4rem+var(--hdr-top))] left-2 right-2 ml-auto max-w-sm max-h-[calc(100vh-5.5rem-var(--hdr-top))] overflow-y-auto no-scrollbar z-50 bg-[#0d141e] border border-slate-700 rounded-xl shadow-2xl animate-slide-up sm:absolute sm:top-auto sm:left-auto sm:right-0 sm:mt-2 sm:w-80 sm:max-w-none sm:max-h-[calc(100vh-5.5rem-var(--hdr-top))] sm:overflow-y-auto" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <span className="font-display text-sm text-slate-200">Pack Signals</span>
          </div>
          {pushSupported() && (
            <button
              data-testid="push-toggle"
              onClick={togglePush}
              disabled={pushBusy || push === "denied"}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs border-b border-slate-800/60 transition-colors ${push === "enabled" ? "text-sky-300 hover:bg-slate-800/40" : "text-slate-400 hover:bg-slate-800/40"} disabled:opacity-50`}
            >
              {push === "enabled" ? <BellRing className="w-4 h-4 text-sky-400" /> : <Bell className="w-4 h-4" />}
              <span className="flex-1 text-left">
                {push === "denied" ? "Alerts blocked in browser settings"
                  : push === "enabled" ? "Push alerts on — tap to turn off"
                  : pushBusy ? "Rousing the pack..." : "Enable push alerts for howls & DMs"}
              </span>
            </button>
          )}
          <div className="max-h-80">
            {notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-slate-500 text-sm">The night is quiet.</div>
            ) : <VList items={notifications} maxHeight={320} rowHeight={64} testid="notification-virtual-list" render={(n) => (
              <button
                key={n.id}
                data-testid="notification-item"
                onClick={() => { onClose(); if (n.link) navigate(n.link); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-800/50 hover:bg-slate-800/40 ${n.read ? "" : "bg-sky-500/5"}`}
              >
                {n.actor ? <Avatar user={n.actor} size="xs" ring={false} /> : <span className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center"><PawPrint className="w-4 h-4 text-amber-400" /></span>}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-slate-300 whitespace-pre-line">{n.text}</span>
                  <span className="block text-[11px] text-slate-500 mt-0.5" data-testid="notification-time" title={n.created_at ? new Date(n.created_at).toLocaleString() : ""}>{timeAgo(n.created_at)}</span>
                </span>
                {!n.read && <span className="w-2 h-2 rounded-full bg-sky-400 shrink-0" />}
              </button>
            )} />}
          </div>
          <button onClick={() => { onClose(); navigate("/signals"); }} data-testid="notification-see-all" className="w-full text-center px-4 py-3 text-xs font-semibold text-sky-300 hover:bg-slate-800/40 border-t border-slate-800">
            See all signals
          </button>
        </div>
      )}
    </div>
  );
}
